import axios from "axios";

// Backend API endpoint
const API_BASE_URL = "/api";
// const API_BASE_URL = "http://localhost:4000/api";

// ============================================
// Safe getter helper (prevents undefined errors)
const get = (obj, prop, defaultValue = null) => obj?.[prop] ?? defaultValue;

// ============================================
// NEW: Define your 8 key fields here (add/remove as needed)
const KEY_FIELDS = [
  "pJiesuan", // Settlement amount
  "pCabinetid", // Cabinet ID
  "pShopName", // Shop/location
  "fUsername", // User name
  "pOrderid", // Primary order ID
  "pAliorderid", // Alt order ID
  "billingDuration", // Usage duration
  "pBorrowtime", // Borrow/start time
  "pGhtime", // Return/end time
  // Add more if needed: 'pType', 'pJifei', 'pBatteryid'
];

// ============================================
// NEW: Extract key fields from full orders array
// ============================================
export const extractKeyFields = (fullOrders) => {
  if (!Array.isArray(fullOrders)) {
    console.warn("⚠️ extractKeyFields: fullOrders is not an array");
    return [];
  }

  return fullOrders.map((order) => {
    const extracted = {};
    KEY_FIELDS.forEach((field) => {
      extracted[field] = get(order, field, null); // Safe extraction
    });
    return extracted; // { pCabinetid: 'BJH05084', pShopName: 'THE REDROOM', ... }
  });
};

// ============================================
// FUNCTION 1: Get all orders with shop names (now extracts keys)
// ============================================
// Backend enriches missing shop names; we extract keys post-fetch
// services/api.js

// import axios from "axios";

// const API_BASE_URL = "http://localhost:4000/api"; // Keep your current backend

// OLD: Remove or comment this out completely
// export const fetchAllOrders = async (fullData = false) => { ... }

// NEW: Simple, clean function that talks to your smart backend
export const fetchAllOrders = async (dateRange = null) => {
  try {
    console.log("Fetching orders with dateRange:", dateRange);

    // CASE 1: No date range → default route (cached latest 3000)
    if (!dateRange || !dateRange.start || !dateRange.end) {
      const response = await axios.get(`${API_BASE_URL}/orders`, {
        timeout: 300000,
      });
      console.log(
        `Fetched ${response.data.totalRecords} orders (default/cached)`
      );
      return {
        full: response.data.data,
        keys: extractKeyFields(response.data.data),
      };
    }

    // CASE 2: With date range → send Kenya time (user sees this)
    const { start, end } = dateRange;

    // Format exactly as your backend expects: YYYY-MM-DD HH:mm:ss (Kenya time)
    const formatDate = (date) => {
      return date.toLocaleString("sv").replace("T", " ").slice(0, 19); // "2025-04-01 00:00:00"
    };

    const sTime = formatDate(start);
    const eTime = formatDate(end);

    const url = `${API_BASE_URL}/orders?sTime=${encodeURIComponent(
      sTime
    )}&eTime=${encodeURIComponent(eTime)}`;
    console.log("Fetching with dates:", url);

    const response = await axios.get(url, { timeout: 300000 });

    console.log(`Fetched ${response.data.totalRecords} orders (custom range)`);
    return {
      full: response.data.data,
      keys: extractKeyFields(response.data.data),
    };
  } catch (error) {
    console.error(
      "fetchAllOrders failed:",
      error.response?.data || error.message
    );
    return { full: [], keys: [] };
  }
};

// ============================================
// FUNCTION 2: Build location map from orders
// ============================================
// Takes orders array and creates { cabinetId -> shopName } mapping
export const buildLocationMap = (orders) => {
  const map = {};

  if (!Array.isArray(orders)) {
    console.warn("⚠️ buildLocationMap: orders is not an array");
    return map;
  }

  orders.forEach((order) => {
    const cabinetId = get(order, "pCabinetid");
    const shopName = get(order, "pShopName", "Unassigned");

    if (cabinetId) {
      map[cabinetId] = shopName;
    }
  });

  console.log(`🗺️ Built location map with ${Object.keys(map).length} cabinets`);
  return map;
};

// ============================================
// FUNCTION 3: Build category metrics from orders
// ============================================
// Groups cabinet IDs by category (AIRPORT, ENT. FAC, MALLS / HOSPITAL)
// Cabinet -> Category mapping (update this if categories change)
const CABINET_CATEGORY_MAP = {
  // AIRPORTS
  DTN00775: "AIRPORT",
  DTN00771: "AIRPORT",
  DTN00769: "AIRPORT",
  DTN00766: "AIRPORT",

  // ENTERTAINMENT FACILITIES
  DTN00773: "ENT. FAC",
  DTN00772: "ENT. FAC",
  DTN00770: "ENT. FAC",
  DTN00767: "ENT. FAC",
  BJH05101: "ENT. FAC",
  BJH05100: "ENT. FAC",
  BJH05091: "ENT. FAC",
  BJH05097: "ENT. FAC",
  BJH05096: "ENT. FAC",
  BJH05094: "ENT. FAC",
  BJH05092: "ENT. FAC",
  BJH05084: "ENT. FAC",
  BJH05083: "ENT. FAC",

  // MALLS & HOSPITALS
  DTN00765: "MALLS / HOSPITAL",
  BJH05095: "MALLS / HOSPITAL",
  BJH05093: "MALLS / HOSPITAL",
  BJH05086: "MALLS / HOSPITAL",
};

export const buildCategoryMetrics = (orders) => {
  const categories = {
    AIRPORT: [],
    "ENT. FAC": [],
    "MALLS / HOSPITAL": [],
    OTHER: [],
  };

  if (!Array.isArray(orders)) {
    console.warn("⚠️ buildCategoryMetrics: orders is not an array");
    return categories;
  }

  // NEW: Track all cabinetIds seen for debugging
  const seenCabinets = new Set();

  orders.forEach((order, index) => {
    const cabinetId = get(order, "pCabinetid"); // Only needs this field now

    if (cabinetId) {
      // NEW: Log each order's cabinet for debugging
      console.log(
        `Order ${index + 1}: Cabinet ID = ${cabinetId} (from order:`,
        order,
        ")"
      );

      if (!seenCabinets.has(cabinetId)) {
        seenCabinets.add(cabinetId);
      }

      const category = CABINET_CATEGORY_MAP[cabinetId] || "OTHER";
      categories[category].push(cabinetId);

      // NEW: Log assignment
      console.log(`  → Assigned to category: ${category}`);
    } else {
      console.log(`Order ${index + 1}: No cabinetId found (order:`, order, ")");
    }
  });

  Object.keys(categories).forEach((cat) => {
    const uniqueCount = new Set(categories[cat]).size;
    console.log(
      `   ${cat}: ${categories[cat].length} total (unique: ${uniqueCount})`
    );
  });

  return categories;
};
